import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useStoreConfig } from "../../features/config/queries";

interface GoogleCredentialResponse {
  credential: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (
            el: HTMLElement,
            options: { theme?: string; size?: string; width?: number; text?: string }
          ) => void;
        };
      };
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.94A9 9 0 0 0 0 9c0 1.45.35 2.83.94 4.03l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .94 4.97l3.01 2.33C4.66 5.17 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}

/** Renders nothing until an admin has configured a Google Client ID in Settings. */
export function GoogleSignInButton({ onCredential }: { onCredential: (idToken: string) => void }) {
  const { data: config } = useStoreConfig();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const realButtonRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);
  // Ref, not a dependency — the caller passes a fresh closure every render,
  // and re-running init/renderButton on every render would flicker the button.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  const clientId = config?.googleClientId;

  useEffect(() => {
    if (!clientId || !wrapperRef.current || !realButtonRef.current) return;
    let cancelled = false;
    const wrapper = wrapperRef.current;
    const realButton = realButtonRef.current;

    loadGoogleScript().then(() => {
      if (cancelled || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredentialRef.current(response.credential)
      });

      // Google's rendered button can't be restyled to match an arbitrary theme
      // (it always looks like Google's own chrome), so it's rendered invisibly
      // at the exact size of our own themed button underneath and stretched to
      // cover it — clicks land on the real button, only our visuals show.
      const render = () => {
        if (!realButton.isConnected) return;
        realButton.innerHTML = "";
        window.google!.accounts.id.renderButton(realButton, {
          theme: "outline",
          size: "large",
          width: Math.round(wrapper.getBoundingClientRect().width),
          text: "continue_with"
        });
      };

      render();
      const observer = new ResizeObserver(render);
      observer.observe(wrapper);
      return () => observer.disconnect();
    });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full h-14"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div
        className={clsx(
          "h-14 w-full px-8 rounded-full border text-base font-medium transition-colors duration-300 ease-physical",
          "inline-flex items-center justify-center gap-3 bg-surface text-ink pointer-events-none",
          hovering ? "border-accent" : "border-border"
        )}
      >
        <GoogleLogo />
        Continue with Google
      </div>
      <div
        ref={realButtonRef}
        aria-label="Continue with Google"
        className="absolute inset-0 overflow-hidden opacity-0 [&_iframe]:!w-full"
      />
    </div>
  );
}
